import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import dayjs from 'dayjs';
import { ApiUrlService } from './api-url.service'; 

export interface MessageData {
  pan?: string;
  transactionAmount?: string;
  surchargeAmount?: string;
  [key: string]: any; // Allow other properties that might exist in the message
}

// Raw transaction shape from the HTTP API response
export interface RawTransaction {
  transactionId: string;
  epochNanoSeconds: string;
  tenantId: string;
  merchantTerminalId: string;
  hostTerminalId?: string;
  transactionType?: string;
  hostResponseCode?: string;
  messageFrom: string;
  messageTo: string;
  messageType: string;
  message?: string;
  pan4?: string;
  totalAmount?: string;
  processor?: string;
  sequenceNumber?: string;
  hostResponseCodeText?: string;
  hostUrl?: string;
  hostPort?: string;
  originCategory?: TransactionCategory;
  userEmail?: string;
}

export const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  TERMINAL_ACTIVITY: 'Terminal',
  SYSTEM_GENERATED: 'System',
  ADMIN_AUDIT: 'Admin',
};

// Categories whose rows can carry a user (userEmail)
export const USER_ATTRIBUTED_CATEGORIES: readonly TransactionCategory[] = [
  'ADMIN_AUDIT',
  'TERMINAL_ACTIVITY',
];

// UI-enriched transaction with computed fields
export interface Transaction extends RawTransaction {
  isoTimestamp?: string;
  total?: string;
  totalColumnFilter?: number | null;
  timestampDate?: Date;
  hostResponseIsApproved?: boolean | null;
  hostTerminalId: string;
  originCategoryLabel?: string;
}

interface RawTransactionResponse {
  items: RawTransaction[];
  cursor?: string;
  hasMore?: boolean;
}

export interface TransactionResponse {
  items: Transaction[];
  cursor?: string;
  hasMore?: boolean;
}

export type TransactionCategory = 'TERMINAL_ACTIVITY' | 'SYSTEM_GENERATED' | 'ADMIN_AUDIT';

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  constructor(
    private http: HttpClient,
    private apiUrlService: ApiUrlService
  ) {
  }

  getTransactions(
    fromDate: Date,
    toDate: Date,
    tenantId: string,
    categories: TransactionCategory[] = ['TERMINAL_ACTIVITY'],
    cursor?: string
  ): Observable<TransactionResponse> {
    const baseUrl = this.apiUrlService.buildApiUrl(tenantId);
    let url = `${baseUrl}/transactions?from=${fromDate.toISOString()}&to=${toDate.toISOString()}&category=${categories.join(',')}`;
    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }
    return this.http.get<RawTransactionResponse>(url).pipe(
      map((response) => {
        return {
          items: (response.items || []).map(tx => {
            return {
              ...tx,
              isoTimestamp: this.convertEpochNanoToISO(tx.epochNanoSeconds),
              timestampDate: (() => {
                const d = new Date(
                  Number(BigInt(tx.epochNanoSeconds) / BigInt(1000000))
                );
                d.setSeconds(0, 0);
                return d;
              })(),
              hostTerminalId: tx.hostTerminalId || '',
              total: this.formatTotal(Number(tx.totalAmount)),
              totalColumnFilter: Number(tx.totalAmount) > 0 ? Number(tx.totalAmount) : null,
              hostResponseIsApproved: tx.hostResponseCodeText ? (tx.hostResponseCodeText === 'Approved') : null,
              originCategoryLabel: tx.originCategory ? CATEGORY_LABELS[tx.originCategory] : ''
            };
          }),
          cursor: response.cursor,
          hasMore: response.hasMore
        };
      })
    );
  }

  private convertEpochNanoToISO(epochNanoSeconds: string): string {
    const epochMs = Number(BigInt(epochNanoSeconds) / BigInt(1000000));
    return dayjs(epochMs).format('YYYY/MM/DD HH:mm:ss.SSS');
  }

  private formatTotal(amount: number): string {
    return amount > 0 ? `${amount.toFixed(2)}` : '';
  }
}
